import './styles.scss';

import { Card, Col, Row } from 'react-bootstrap';
import { ReactElement, ReactNode, useMemo } from 'react';

import DetailsProgressBar from '@/pages/DynamicReport/components/DetailsProgressBar';
import TippyWhen from '@/components/TippyWhen';
import { formatNumber } from '@/utils/numbers';
import { getClasses } from '@/utils/strings';

type DetailsProps = {
  data: any[];
  inline?: boolean;
  className?: string;
  reportType?: string;
  [x: string]: any;
};

const Details = ({ data, inline, className, reportType, ...props }: DetailsProps): JSX.Element => {
  const Content = useMemo((): JSX.Element | JSX.Element[] => {
    const items = (data || [])
      .map((group: any, g: number): JSX.Element[] =>
        Object.entries(group).map(
          ([key, { name, description, value }]: [string, { name: string; description: string; value: string }], d: number): JSX.Element => (
            <Item
              className={getClasses(inline === undefined ? 'col' : undefined, !d ? 'group' : undefined)}
              name={key}
              value={value}
              title={name}
              description={description}
              key={`${g}-${d}`}
            />
          )
        )
      )
      .flat();
    return items;
  }, [data, inline]);

  return (
    <Card {...props} className={getClasses('Details', inline !== undefined ? 'inline' : undefined, className)}>
      <Card.Body>
        <Row
          className={getClasses(
            reportType === 'charityReport' ? 'justify-content-center gap-10' : inline === undefined ? 'justify-content-around' : ''
          )}
        >
          {Content}
        </Row>
      </Card.Body>
    </Card>
  );
};

// Generic type/value splitter: captures type in group 1 and payload in group 2
const TYPE_PAYLOAD_RE = /^([^:]+):(.+)$/;
// Numeric payload regex for progress-bar: captures part, total, percent in groups 1..3
const PROGRESS_BAR_NUM_RE = /^(-?\d+(?:\.\d+)?)\|(-?\d+(?:\.\d+)?)\|(-?\d+(?:\.\d+)?)$/;

type ItemProps = {
  name: string;
  value?: string | number | null;
  title?: string;
  description?: ReactNode;
  [x: string]: any;
};

const Item = ({ name, value, title: _title, description, ...props }: ItemProps): JSX.Element => {
  const raw = typeof value === 'string' ? value : String(value ?? '');

  const typeMatch = raw.match(TYPE_PAYLOAD_RE);
  const type = typeMatch ? typeMatch[1] : null;
  const matchForRender = typeMatch || null;
  // Inline the switch here so the JSX output is easy to inspect in-place
  let inner: ReactElement;
  switch (type) {
    case 'progress-bar': {
      const payload = matchForRender?.[2] ?? '';
      const numMatch = payload.match(PROGRESS_BAR_NUM_RE);
      const current = numMatch ? Number.parseFloat(numMatch[1]) || 0 : 0;
      const goal = numMatch ? Number.parseFloat(numMatch[2]) || 0 : 0;
      const percent = numMatch ? Number.parseFloat(numMatch[3]) || 0 : 0;
      const leftLabel = '$' + formatNumber.currency(Math.trunc(current), { maximumFractionDigits: 0, minimumFractionDigits: 0 });
      const rightLabel = '$' + formatNumber.currency(Math.trunc(goal), { maximumFractionDigits: 0, minimumFractionDigits: 0 });
      inner = <DetailsProgressBar name={name} leftLabel={leftLabel} rightLabel={rightLabel} now={Math.max(0, Math.min(100, percent))} />;
      break;
    }
    default:
      inner = (
        <>
          <span>{name}</span>
          <span>{String(value ?? '')}</span>
        </>
      );
  }

  return (
    <Col>
      <TippyWhen
        options={{
          content: (
            <div className="text-center {max-width:15rem;}">
              {name && <strong className="d-block {font-size:1rem!;}">{name}</strong>}
              {description && <span>{description}</span>}
            </div>
          ),
        }}
        isTrue={!!name || !!description}
      >
        <div {...props} className="DetailsItem" data-name={name}>
          {inner}
        </div>
      </TippyWhen>
    </Col>
  );
};

export default Details;
